/**
 * BOM tab — right panel and constants for the visual editor's BOM mode.
 * Extracted from visual-style-file-page.tsx.
 *
 * Exports BOM pin constants so the parent page can render `DraggablePin`
 * overlays on the canvas.
 */
import { ArrowLeft } from "lucide-react";
import { KV, Placeholder } from "./canvas-helpers.tsx";

/* ── Types ────────────────────────────────────────────────────────────── */

export interface BomPin {
  bomLineId: string;
  kind: string;
  label: string;
  uom: string;
  requiredQtyMilli: number;
  pinX: number | null;
  pinY: number | null;
  pinView: "front" | "back" | null;
  anchorX: number;
  anchorY: number;
}

/* ── Constants ────────────────────────────────────────────────────────── */

/** Where each BOM kind anchors before it's placed (% of stage). */
export const BOM_KIND_ANCHOR: Record<string, { x: number; y: number }> = {
  fabric: { x: 50, y: 58 },
  trim: { x: 50, y: 22 },
  packing: { x: 50, y: 88 },
  process: { x: 84, y: 44 },
};
export const BOM_KIND_DOT: Record<string, string> = {
  fabric: "bg-sky-500",
  trim: "bg-violet-500",
  packing: "bg-amber-500",
  process: "bg-emerald-500",
};
export const BOM_KIND_RING: Record<string, string> = {
  fabric: "ring-sky-500/30",
  trim: "ring-violet-500/30",
  packing: "ring-amber-500/30",
  process: "ring-emerald-500/30",
};

/* ── Panel ────────────────────────────────────────────────────────────── */

export function BomPanel({
  pins,
  selected,
  unplaced,
  onPick,
  onBack,
}: {
  pins: BomPin[];
  selected: BomPin | null;
  unplaced: number;
  onPick: (id: string) => void;
  onBack: () => void;
}) {
  if (pins.length === 0) {
    return (
      <Placeholder
        title="Bill of materials"
        body="The BOM is generated when costing is locked (§4). Once it exists, each material maps to a hotspot on the garment."
      />
    );
  }

  if (selected) {
    const placed = selected.pinX !== null && selected.pinY !== null;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`h-3 w-3 shrink-0 rounded-full ${BOM_KIND_DOT[selected.kind] ?? "bg-sky-500"}`}
          />
          <h3 className="flex-1 text-sm font-semibold text-ink">{selected.label}</h3>
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to BOM list"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        </div>
        <dl className="flex flex-col gap-1.5 text-sm">
          <KV
            k="Kind"
            v={<span className="capitalize">{selected.kind}</span>}
          />
          <KV
            k="Requirement"
            v={`${(selected.requiredQtyMilli / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 })} ${selected.uom}`}
          />
          <KV
            k="Placed"
            v={
              placed ? (
                <span className="capitalize text-ink-2">
                  {selected.pinView} · {selected.pinX?.toFixed(0)},
                  {selected.pinY?.toFixed(0)}
                </span>
              ) : (
                <span className="text-muted">Not yet — drag it</span>
              )
            }
          />
        </dl>
        <p className="text-xs text-muted">
          The hotspot marks where this material goes on the garment. The
          requirement is set at costing-lock (§4); only the pin position is set
          here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">Bill of materials</h3>
      {unplaced > 0 ? (
        <p className="rounded-sm border border-dashed border-line bg-bg px-2.5 py-2 text-xs text-muted">
          {unplaced} hotspot{unplaced === 1 ? "" : "s"} not placed yet — drag{" "}
          {unplaced === 1 ? "it" : "them"} onto the garment.
        </p>
      ) : null}
      <ul className="flex flex-col gap-1">
        {pins.map((l) => (
          <li key={l.bomLineId}>
            <button
              type="button"
              onClick={() => onPick(l.bomLineId)}
              className="flex w-full items-center gap-2 rounded-sm border border-line bg-paper px-2 py-1.5 text-left text-xs transition hover:bg-bg"
            >
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${BOM_KIND_DOT[l.kind] ?? "bg-sky-500"}`}
              />
              <span className="min-w-0 flex-1 truncate font-medium text-ink">
                {l.label}
              </span>
              <span className="shrink-0 font-mono text-muted">
                {(l.requiredQtyMilli / 1000).toLocaleString("en-IN", {
                  maximumFractionDigits: 1,
                })}{" "}
                {l.uom}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
